import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { PageShell } from './layout';
import useHasEverAuthenticated from '../hooks/useHasEverAuthenticated';
import { fetchAIHelperHistory } from '../api/aiHelper';
import { GAME_CONFIG } from '../games';
import { buildTrackedAuthRedirectUrl } from '../analytics';
import Button from './Button';
import FaIcon from './FaIcon';
import styles from '../styles/AIHelperLandingPage.module.css';
import buttonStyles from '../styles/Button.module.css';

// --------------------------------------------------------------------------
// Custom large AI Hero Icon — unique IDs to avoid SVG conflicts
// --------------------------------------------------------------------------

const HeroAIIcon = ({ size = 120, style = {}, className = '' }) => {
  const uniqueId = React.useId();
  return (
    <svg
      className={className}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      style={style}
    >
      <defs>
        <linearGradient id={`ai-hero-bg-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#7c3aed" />
          <stop offset="50%" stopColor="#8b5cf6" />
          <stop offset="100%" stopColor="#a78bfa" />
        </linearGradient>
        <linearGradient id={`ai-hero-shine-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#c4b5fd" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#7c3aed" stopOpacity="0" />
        </linearGradient>
        <filter id={`ai-hero-glow-${uniqueId}`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="3" result="glow" />
          <feMerge>
            <feMergeNode in="glow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id={`ai-hero-outer-glow-${uniqueId}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="6" result="outerGlow" />
          <feMerge>
            <feMergeNode in="outerGlow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Main circle background */}
      <circle
        cx="60"
        cy="60"
        r="52"
        fill={`url(#ai-hero-bg-${uniqueId})`}
        stroke="#6d28d9"
        strokeWidth="1.5"
        filter={`url(#ai-hero-outer-glow-${uniqueId})`}
      />

      {/* Inner highlight ring */}
      <circle
        cx="60"
        cy="60"
        r="44"
        fill="none"
        stroke={`url(#ai-hero-shine-${uniqueId})`}
        strokeWidth="1"
        opacity="0.5"
      />

      {/* Inner glow disc */}
      <circle cx="60" cy="60" r="36" fill="#a78bfa" opacity="0.15" />

      {/* Main sparkle star — center, large */}
      <g filter={`url(#ai-hero-glow-${uniqueId})`}>
        <path
          d="M60 30 L63.5 52 L60 56 L56.5 52 Z"
          fill="#ede9fe"
        />
        <path
          d="M60 90 L56.5 68 L60 64 L63.5 68 Z"
          fill="#ede9fe"
        />
        <path
          d="M30 60 L52 56.5 L56 60 L52 63.5 Z"
          fill="#ede9fe"
        />
        <path
          d="M90 60 L68 63.5 L64 60 L68 56.5 Z"
          fill="#ede9fe"
        />
        {/* Diagonal arms */}
        <path
          d="M60 46 L64 56 L60 60 L56 56 Z"
          fill="#f5f3ff"
          opacity="0.9"
        />
        <path
          d="M60 74 L56 64 L60 60 L64 64 Z"
          fill="#f5f3ff"
          opacity="0.9"
        />
      </g>

      {/* Small sparkle — top right */}
      <g transform="translate(78, 32)" filter={`url(#ai-hero-glow-${uniqueId})`}>
        <path
          d="M0 -8 L1.8 -1.8 L0 0 L-1.8 -1.8 Z"
          fill="#ddd6fe"
        />
        <path
          d="M0 8 L-1.8 1.8 L0 0 L1.8 1.8 Z"
          fill="#ddd6fe"
        />
        <path
          d="M-8 0 L-1.8 -1.8 L0 0 L-1.8 1.8 Z"
          fill="#ddd6fe"
        />
        <path
          d="M8 0 L1.8 1.8 L0 0 L1.8 -1.8 Z"
          fill="#ddd6fe"
        />
      </g>

      {/* Small sparkle — bottom left */}
      <g transform="translate(38, 80)" filter={`url(#ai-hero-glow-${uniqueId})`}>
        <path
          d="M0 -6 L1.4 -1.4 L0 0 L-1.4 -1.4 Z"
          fill="#ddd6fe"
        />
        <path
          d="M0 6 L-1.4 1.4 L0 0 L1.4 1.4 Z"
          fill="#ddd6fe"
        />
        <path
          d="M-6 0 L-1.4 -1.4 L0 0 L-1.4 1.4 Z"
          fill="#ddd6fe"
        />
        <path
          d="M6 0 L1.4 1.4 L0 0 L1.4 -1.4 Z"
          fill="#ddd6fe"
        />
      </g>

      {/* Tiny accent dots */}
      <circle cx="82" cy="52" r="2" fill="#c4b5fd" opacity="0.8" />
      <circle cx="40" cy="38" r="1.5" fill="#c4b5fd" opacity="0.6" />
      <circle cx="75" cy="82" r="1.5" fill="#c4b5fd" opacity="0.7" />
    </svg>
  );
};

// --------------------------------------------------------------------------
// Interactive demo: simulated desktop showing a completed Collegamenti game
// with the AI Assistant panel sliding in from the right
// --------------------------------------------------------------------------

const DEMO_GROUPS = [
  { theme: 'Animali della fattoria', color: '#f7cf1c', words: ['CAVALLO', 'MAIALE', 'OCA', 'TACCHINO'] },
  { theme: 'Parti della casa', color: '#8ab72f', words: ['SALOTTO', 'MANSARDA', 'STUDIO', 'ATRIO'] },
  { theme: 'Tipi di pasta', color: '#7b9ce1', words: ['TROFIE', 'PENNE', 'LINGUINE', 'RADIATORI'] },
  { theme: 'Colori', color: '#b76dc5', words: ['INDACO', 'OCRA', 'MALVA', 'VERDE'] },
];

const DEMO_TARGET_WORD = 'TROFIE';
const DEMO_TARGET_GROUP = 2;

/* eslint-disable react/jsx-key */
const DEMO_AI_LINES = [
  <><strong>Trofie</strong> sono un tipo di pasta corta originaria della Liguria.</>,
  null,
  <>Tipicamente servite con il <em>pesto alla genovese</em>, il nome potrebbe derivare dal genovese <em>strufuggiâ</em> (strofinare), il gesto di arrotolare l&apos;impasto.</>,
  null,
  <>Esempio: <em>Le trofie al pesto sono uno dei piatti più iconici della cucina ligure.</em></>,
];
/* eslint-enable react/jsx-key */

function InteractiveDemo() {
  // Phases: idle → cursorMove → click → panelIn → cursorBtn → btnClick → loading → typing → done
  const [phase, setPhase] = useState('idle');
  const [typedLines, setTypedLines] = useState(0);
  const timerRef = useRef(null);
  const screenRef = useRef(null);
  const wordRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const after = (fn, ms) => {
      timerRef.current = setTimeout(() => { if (mounted) fn(); }, ms);
    };

    const cycle = () => {
      if (!mounted) return;
      setPhase('idle');
      setTypedLines(0);

      after(() => {
        setPhase('cursorMove');
        after(() => {
          setPhase('click');
          after(() => {
            setPhase('panelIn');
            after(() => {
              setPhase('cursorBtn');
              after(() => {
                setPhase('btnClick');
                after(() => {
                  setPhase('loading');
                  after(() => {
                    setPhase('typing');
                    let line = 0;
                    const typeLine = () => {
                      if (!mounted) return;
                      line++;
                      setTypedLines(line);
                      if (line < DEMO_AI_LINES.length) {
                        after(typeLine, 350);
                      } else {
                        setPhase('done');
                        after(cycle, 4000);
                      }
                    };
                    after(typeLine, 400);
                  }, 1000);
                }, 400);
              }, 900);
            }, 800);
          }, 600);
        }, 1100);
      }, 2000);
    };

    cycle();
    return () => { mounted = false; clearTimeout(timerRef.current); };
  }, []);

  const showCursor = ['cursorMove', 'click', 'panelIn', 'cursorBtn', 'btnClick'].includes(phase);
  const cursorClicking = phase === 'click';
  const showHL = !['idle', 'cursorMove'].includes(phase);
  const panelOpen = ['panelIn', 'cursorBtn', 'btnClick', 'loading', 'typing', 'done'].includes(phase);
  const showConfirm = ['panelIn', 'cursorBtn', 'btnClick'].includes(phase);
  const showLoading = phase === 'loading';
  const showResp = ['typing', 'done'].includes(phase);
  const btnPressed = phase === 'btnClick';

  // Helper: compute pixel position of a target element's center relative to demoScreen
  const getTargetPos = (targetRef) => {
    if (!screenRef.current || !targetRef.current) return null;
    const sr = screenRef.current.getBoundingClientRect();
    const tr = targetRef.current.getBoundingClientRect();
    return {
      top: `${tr.top + tr.height / 2 - sr.top}px`,
      left: `${tr.left + tr.width / 2 - sr.left}px`,
    };
  };

  // Cursor position (px within .demoScreen, computed from actual element positions)
  const cursorAt = (() => {
    switch (phase) {
      case 'cursorMove':
      case 'click':
      case 'panelIn':
        return getTargetPos(wordRef) || { top: '57%', left: '30%' };
      case 'cursorBtn':
      case 'btnClick':
        return getTargetPos(btnRef) || { top: '50%', left: '82%' };
      default:
        return { top: '82%', left: '10%' };
    }
  })();

  return (
    <div className={styles.demoWrapper}>
      <div className={styles.demoScreen} ref={screenRef}>
        {/* Browser chrome */}
        <div className={styles.demoChrome}>
          <div className={styles.demoDots}>
            <span /><span /><span />
          </div>
          <div className={styles.demoUrlBar}>
            <FaIcon className="fas fa-lock"  />
            <span>giochinidiparole.com/collegamenti</span>
          </div>
        </div>

        {/* App body */}
        <div className={styles.demoBody}>
          {/* Game content */}
          <div className={`${styles.demoGameArea} ${panelOpen ? styles.demoGameAreaShifted : ''}`}>
            <div className={styles.demoGameBar}>
              <span className={styles.demoGameName}>Collegamenti</span>
              <span className={styles.demoGameDate}>13 novembre 2025</span>
            </div>

            <div className={styles.demoGroups}>
              {DEMO_GROUPS.map((g, gi) => (
                <div key={gi} className={styles.demoGroup} style={{ backgroundColor: g.color }}>
                  <div className={styles.demoGroupTheme}>{g.theme}</div>
                  <div className={styles.demoGroupWords}>
                    {g.words.map((w, wi) => (
                      <React.Fragment key={w}>
                        {wi > 0 && ', '}
                        <span ref={w === DEMO_TARGET_WORD ? wordRef : undefined} className={w === DEMO_TARGET_WORD && showHL ? styles.demoWordHL : undefined}>{w}</span>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.demoWinBanner}>Hai trovato tutti i gruppi! 🚀</div>
          </div>

          {/* AI Panel */}
          <div className={`${styles.demoPanel} ${panelOpen ? styles.demoPanelOpen : ''}`}>
            <div className={styles.demoPanelHead}>
              <span className={styles.demoPanelTitle}>
                <FaIcon className="fa-solid fa-sparkles"  />Scopri con Lella
              </span>
              <FaIcon className="fa-solid fa-xmark" style={{ color: '#aaa', fontSize: '0.7rem' }}  />
            </div>

            <div className={styles.demoPanelPill}>
              <span className={styles.demoPanelPillLabel}>Selezione:</span>
              <span className={styles.demoPanelPillValue}>{DEMO_TARGET_WORD}</span>
            </div>

            {showConfirm && (
              <div className={styles.demoPanelConfirm}>
                <div ref={btnRef} className={`${styles.demoPanelAskBtn} ${btnPressed ? styles.demoPanelAskBtnPressed : ''}`}>
                  <FaIcon className="fa-solid fa-sparkles"  /> Chiedi a Lella
                </div>
              </div>
            )}

            {showLoading && (
              <div className={styles.demoPanelShimArea}>
                <div className={styles.demoShim} />
                <div className={`${styles.demoShim} ${styles.demoShimW70}`} />
                <div className={`${styles.demoShim} ${styles.demoShimW50}`} />
              </div>
            )}

            {showResp && (
              <div className={styles.demoPanelResp}>
                {DEMO_AI_LINES.slice(0, typedLines).map((line, i) => (
                  <p key={i} className={styles.demoPanelRespLine}>{line ?? '\u00A0'}</p>
                ))}
                {phase === 'typing' && <span className={styles.demoBlinkCaret}>|</span>}
              </div>
            )}
          </div>
        </div>

        {/* Animated cursor */}
        <div
          className={`${styles.demoCursorIcon} ${showCursor ? styles.demoCursorIconShow : ''} ${cursorClicking ? styles.demoCursorClick : ''}`}
          style={{ top: cursorAt.top, left: cursorAt.left }}
        >
          <svg width="18" height="22" viewBox="0 0 18 22" fill="none">
            <path d="M1 1L1 17L5.5 12.5L9.5 20L12 18.5L8 11L14 10L1 1Z" fill="#fff" stroke="#222" strokeWidth="1.2" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Data
// --------------------------------------------------------------------------

const FEATURES = [
  {
    icon: 'fa-language',
    title: 'Significato in contesto',
    description: 'Scopri il significato di parole ed espressioni direttamente nel contesto del gioco che stai risolvendo.',
    gradient: 'linear-gradient(145deg, #8b5cf6, #7c3aed)',
  },
  {
    icon: 'fa-graduation-cap',
    title: 'Spiegazioni semplici',
    description: 'Le risposte sono pensate per studenti di italiano: chiare, in italiano semplice, con esempi pratici.',
    gradient: 'linear-gradient(145deg, #6366f1, #4f46e5)',
  },
  {
    icon: 'fa-book-open',
    title: 'Curiosità linguistiche',
    description: "Approfondisci sfumature culturali, espressioni idiomatiche e modi di dire.",
    gradient: 'linear-gradient(145deg, #a855f7, #9333ea)',
  },
];

const STEPS = [
  {
    number: '1',
    title: 'Completa la partita',
    description: "Lella si attiva solo a partita conclusa.",
    icon: 'fa-gamepad',
  },
  {
    number: '2',
    title: 'Tocca una parola',
    description: 'Tocca o clicca su una parola che vuoi approfondire.',
    icon: 'fa-hand-pointer',
  },
  {
    number: '3',
    title: 'Chiedi a Lella',
    description: 'Conferma la richiesta e Lella ti spiegherà tutto in pochi secondi.',
    icon: 'fa-wand-magic-sparkles',
  },
];

const BENEFITS = [
  {
    emoji: '🎯',
    title: 'Impara giocando',
    description: 'Trasforma ogni partita in un\'occasione per ampliare il tuo vocabolario e la tua comprensione dell\'italiano.',
  },
  {
    emoji: '⚡',
    title: 'Risposte istantanee',
    description: 'Non serve cercare su un dizionario esterno: trovi la spiegazione direttamente dentro il gioco.',
  },
  {
    emoji: '🧠',
    title: 'Contesto, non solo definizioni',
    description: 'L\'assistente non ti dà solo la definizione: ti spiega perché quella parola è usata lì, in quel modo.',
  },
];

const FAQ = [
  {
    q: 'Quanto costa usare l\'assistente AI?',
    a: 'Ogni richiesta richiede gettoncini. I gettoncini si guadagnano gratuitamente giocando: non serve comprare nulla.',
  },
  {
    q: 'Quando posso usarlo?',
    a: 'L\'assistente si attiva a partita conclusa (vinta o persa). Durante il gioco non è disponibile, per non togliere il divertimento della sfida.',
  },
  {
    q: 'In che lingua risponde?',
    a: 'Le risposte sono in italiano, con un linguaggio semplice e adatto a studenti di tutti i livelli.',
  },
  {
    q: 'Posso usarlo su qualsiasi gioco?',
    a: 'Sì! L\'assistente funziona su tutti i giochi della piattaforma: basta toccare una parola dopo aver completato la partita.',
  },
];

// --------------------------------------------------------------------------
// Game slug → display helpers
// --------------------------------------------------------------------------

const GAME_DISPLAY = Object.fromEntries(
  Object.entries(GAME_CONFIG).map(([slug, cfg]) => [
    slug,
    {
      displayName: cfg.displayName,
      pathName: cfg.pathName,
      color: cfg.backgroundColor,
    },
  ])
);

/**
 * Format a date string (YYYY-MM-DD or ISO) into a readable Italian date.
 */
function formatDate(dateStr) {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

// --------------------------------------------------------------------------
// History section (only for authenticated users)
// --------------------------------------------------------------------------

function HistorySection({ onHasEntries }) {
  const { getToken } = useAuth();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);   // initial load
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const LIMIT = 12;

  const loadHistory = useCallback(
    async (currentEntries = []) => {
      const isInitial = currentEntries.length === 0;
      if (isInitial) setLoading(true); else setLoadingMore(true);
      setError(null);
      try {
        const token = await getToken({ template: 'basic_user_info_jwt_template' }).catch(
          () => null
        );
        const data = await fetchAIHelperHistory(token, {
          limit: LIMIT,
          offset: currentEntries.length,
        });
        setEntries([...currentEntries, ...data.entries]);
        setTotal(data.total);
      } catch {
        setError('Impossibile caricare la cronologia.');
      } finally {
        if (isInitial) setLoading(false); else setLoadingMore(false);
      }
    },
    [getToken]
  );

  useEffect(() => {
    loadHistory([]);
  }, [loadHistory]);

  // Notify parent about whether the user has history entries
  useEffect(() => {
    if (!loading && onHasEntries) {
      onHasEntries(entries.length > 0);
    }
  }, [loading, entries.length, onHasEntries]);

  // Don't render the section at all if there's nothing and we're not loading
  if (!loading && !error && entries.length === 0) return null;

  const hasMore = entries.length < total;

  return (
    <section className={styles.historySection}>
      <div className={styles.sectionContainer}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionEmoji}>📚</span>
          <h2 className={styles.sectionTitle}>Le tue ricerche</h2>
          <p className={styles.sectionSubtitle}>
            Rivedi le spiegazioni che hai ricevuto grazie alla intelligenza di Lella.
          </p>
        </div>

        {loading && (
          <div className={styles.historyLoading}>
            <FaIcon className="fas fa-spinner fa-spin"  />
            <span>Caricamento…</span>
          </div>
        )}

        {error && (
          <div className={styles.historyError}>
            <FaIcon className="fas fa-exclamation-circle"  />
            <span>{error}</span>
            <button onClick={() => loadHistory(offset)} className={styles.historyRetry}>
              Riprova
            </button>
          </div>
        )}

        {!loading && !error && entries.length > 0 && (
          <>
            <div className={styles.historyGrid}>
              {entries.map((entry) => {
                const game = GAME_DISPLAY[entry.game_slug] || {};
                const gameName = game.displayName || entry.game_slug;
                const gamePath = game.pathName || entry.game_path_name || entry.game_slug;
                const gameColor = game.color || '#8b5cf6';
                const linkTo = `/${gamePath}/${entry.puzzle_date}?ai-entry=${entry.id}`;

                return (
                  <Link key={entry.id} to={linkTo} className={styles.historyCard}>
                    <div className={styles.historyCardHeader}>
                      <span
                        className={styles.historyGameBadge}
                        style={{ background: gameColor }}
                      >
                        {gameName}
                      </span>
                      <span className={styles.historyDate}>
                        {formatDate(entry.puzzle_date)}
                      </span>
                    </div>
                    <div className={styles.historySelectedText}>
                      <FaIcon className="fas fa-quote-left"  />
                      <span>{entry.selected_text}</span>
                    </div>
                    {entry.response_preview && (
                      <p className={styles.historyPreview}>
                        {entry.response_preview}
                        {entry.response_preview.length >= 140 ? '…' : ''}
                      </p>
                    )}
                    <div className={styles.historyCardFooter}>
                      <span className={styles.historyViewLink}>
                        Rivedi la spiegazione <FaIcon className="fas fa-arrow-right"  />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>

            {hasMore && (
              <div className={styles.historyLoadMore}>
                <button
                  className={styles.historyLoadMoreBtn}
                  disabled={loadingMore}
                  onClick={() => loadHistory(entries)}
                >
                  {loadingMore ? (
                    <>
                      <FaIcon className="fas fa-spinner fa-spin"  />
                      <span>Caricamento…</span>
                    </>
                  ) : (
                    <>
                      <FaIcon className="fas fa-plus"  />
                      <span>Carica altre ricerche</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

const AIHelperLandingPage = () => {
  const { isSignedIn } = useUser();
  const hasEverAuthenticated = useHasEverAuthenticated();
  const navigate = useNavigate();
  // null = unknown/loading, true = has entries, false = no entries
  const [hasHistory, setHasHistory] = useState(null);

  const handleAuthClick = () => {
    const authUrl = buildTrackedAuthRedirectUrl({
      cta_kind: hasEverAuthenticated ? 'sign_in' : 'sign_up',
      source_surface: 'ai_helper_landing',
      gate_reason: 'ai_helper_landing',
      next_path: '/assistente-ai',
      is_returning_device: hasEverAuthenticated,
      track_gate_viewed: true,
      trigger: 'landing_cta',
    });
    navigate(authUrl);
  };

  return (
    <PageShell>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        {/* Floating mini icons background */}
        <div className={styles.floatingIcons}>
          {[...Array(8)].map((_, i) => (
            <div key={i} className={styles.floatingIcon}>
              <HeroAIIcon size={28 + (i % 3) * 14} />
            </div>
          ))}
        </div>

        {/* Sparkle dots */}
        <div className={styles.sparkles}>
          {[...Array(8)].map((_, i) => (
            <div key={i} className={styles.sparkle} />
          ))}
        </div>

        <div className={styles.heroContent}>
          {/* Animated AI icon */}
          <div className={styles.iconShowcase}>
            <div className={styles.mainIcon}>
              <div className={styles.spinWrapper}>
                <HeroAIIcon size={120} />
              </div>
            </div>
            {/* Orbiting particles */}
            <div className={styles.orbitRing}>
              <div className={styles.orbitDot} />
              <div className={`${styles.orbitDot} ${styles.orbitDot2}`} />
              <div className={`${styles.orbitDot} ${styles.orbitDot3}`} />
            </div>
          </div>

          <h1 className={styles.heroTitle}>Ti presentiamo Lella</h1>

          <p className={styles.heroSubtitle}>
            Lella è la tua compagna di studio intraprendente. Tocca una parola 
            dopo aver completato un gioco e scopri subito il significato in contesto.
          </p>

        </div>

        <div className={styles.scrollIndicator}>
          <span>Scopri di più</span>
          <FaIcon className={`fas fa-chevron-down ${styles.scrollArrow}`}  />
        </div>
      </section>

      {/* History Section — only for authenticated users */}
      {isSignedIn && <HistorySection onHasEntries={setHasHistory} />}

     {/* How it Works Section with Interactive Demo */}
     <section className={styles.howSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <img src="/lella.png" alt="Lella" className={styles.sectionLellaAvatar} />
            <h2 className={styles.sectionTitle}>Come funziona</h2>
            <p className={styles.sectionSubtitle}>
              Tre semplici passi per imparare di più da ogni partita.
            </p>
          </div>

          <div className={styles.stepsGrid}>
            {STEPS.map((step, index) => (
              <div key={index} className={styles.stepCard}>
                <div className={styles.stepNumber}>{step.number}</div>
                <div className={styles.stepIconWrapper}>
                  <FaIcon className={`fas ${step.icon}`} />
                </div>
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepDescription}>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Demo — shown early for new/unauthenticated users */}
      {<section className={styles.demoSection}>
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Guarda come funziona</h2>
              <p className={styles.sectionSubtitle}>
                Ecco una dimostrazione dal vivo dell'assistente in azione.
              </p>
            </div>

            <InteractiveDemo />
          </div>
        </section>
      }

      {/* What is it Section */}
      <section className={styles.whatSection}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Come ti aiuta Lella?</h2>
          <p className={styles.sectionDescription}>
            Lella ti dà una spiegazione chiara e dettagliata, pensata 
            appositamente per chi sta imparando l'italiano, dando il significato in contesto.
          </p>

          <div className={styles.keyPoints}>
            {FEATURES.map((feature, index) => (
              <div key={index} className={styles.keyPoint}>
                <div
                  className={styles.keyPointIcon}
                  style={{ background: feature.gradient }}
                >
                  <FaIcon className={`fas ${feature.icon}`} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={styles.benefitsSection}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEmoji}>✨</span>
            <h2 className={styles.sectionTitle}>Perché usare l'assistente?</h2>
            <p className={styles.sectionSubtitle}>
              Con Lella, non devi lasciare il gioco per trovare il significato di una parola o capire meglio gli usi. Tutto in un unico posto.
            </p>
          </div>

          <div className={styles.benefitsGrid}>
            {BENEFITS.map((benefit, index) => (
              <div key={index} className={styles.benefitCard}>
                <span className={styles.benefitEmoji}>{benefit.emoji}</span>
                <h3 className={styles.benefitTitle}>{benefit.title}</h3>
                <p className={styles.benefitDescription}>{benefit.description}</p>
              </div>
            ))}
          </div>

          <div className={styles.costNote}>
            <FaIcon className="fas fa-info-circle"  />
            <p>
              Ogni richiesta all'assistente richiede <strong>gettoncini</strong>.
              Guadagni gettoncini gratuitamente completando le partite. <Link to="/gettoncini">Scopri di più →</Link>
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={styles.faqSection}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Domande frequenti</h2>

          <div className={styles.faqGrid}>
            {FAQ.map((item, index) => (
              <div key={index} className={styles.faqItem}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className={styles.bottomCta}>
        <div className={styles.bottomCtaContent}>
          <HeroAIIcon size={80} className={styles.bottomCtaIcon} />
          <h2 className={styles.bottomCtaTitle}>
            {isSignedIn ? 'Prova subito l\'assistente!' : 'Pronto a imparare di più?'}
          </h2>
          <p className={styles.bottomCtaText}>
            {isSignedIn
              ? 'Gioca una partita e tocca una parola per scoprire il potere di Lella.'
              : 'Crea un account gratuito, gioca una partita e scopri quanto è facile approfondire il tuo italiano.'}
          </p>

          {isSignedIn ? (
            <Link to="/" className={styles.bottomCtaButton}>
              <FaIcon className="fas fa-gamepad"  />
              <span>Vai a giocare</span>
            </Link>
          ) : (
            <button className={styles.bottomCtaButton} onClick={handleAuthClick}>
              <FaIcon className="fas fa-user-plus"  />
              <span>{hasEverAuthenticated ? 'Accedi' : 'Registrati gratis'}</span>
            </button>
          )}
        </div>
      </section>
    </PageShell>
  );
};

export default AIHelperLandingPage;
